/** Protected content writes over the existing authenticated CLI API surface. */
type Request = <T>(method: string, path: string, body?: unknown) => Promise<T>;
const id = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || !value.trim() || value.includes('/')) throw new Error(`${label} is required.`);
  return encodeURIComponent(value);
};
export class ContentConsumerTransport {
  constructor(private readonly request: Request) {}
  private async preflight() {
    const result = await this.request<any>('GET', '/api/cli/v1/capabilities');
    if (result?.success !== true || result.data?.features?.transactionalContentConsumerWrites !== true) {
      throw new Error('The server must support protected content saves before this edit can be saved.');
    }
  }
  async saveStrategy(projectId: string, strategy: Record<string, any>): Promise<void> {
    const project = id(projectId, 'Project'); id(strategy.id, 'Strategy'); id(strategy.authorId, 'Author');
    if (strategy.projectId != null && strategy.projectId !== projectId) throw new Error('Strategy belongs to another project.');
    await this.preflight();
    const result = await this.request<any>('POST', `/api/cli/v1/projects/${project}/content-plan/strategies/apply`, {
      strategyId: strategy.id, authorId: strategy.authorId, fields: strategy,
    });
    if (result?.success !== true || result.data?.projectId !== projectId || result.data?.strategyId !== strategy.id || result.data?.dryRun !== false) {
      throw new Error('The server did not confirm this strategy save.');
    }
  }
  async patchTopic(projectId: string, topicId: string, authorId: string, patch: Record<string, unknown>): Promise<void> {
    const project = id(projectId, 'Project'); const topic = id(topicId, 'Topic'); id(authorId, 'Author');
    await this.preflight();
    const result = await this.request<any>('PATCH', `/api/cli/v1/projects/${project}/content-plan/topics/${topic}`, { authorId, patch });
    if (result?.success !== true || result.data?.projectId !== projectId || result.data?.topicId !== topicId || result.data?.dryRun !== false) {
      throw new Error('The server did not confirm this topic save.');
    }
  }
}

/** Independently check the SDK readback of the fields the editor submitted. */
export function assertContentWriteReadback(current: Record<string, any> | undefined, fields: Record<string, any>, mergeMaps = false): void {
  if (!current) throw new Error('Saved content is unavailable on readback.');
  const equal = (actual: any, expected: any, merge: boolean): boolean => {
    if (expected && typeof expected.toJSON === 'function') expected = expected.toJSON();
    if (actual && typeof actual.toJSON === 'function') actual = actual.toJSON();
    if (Array.isArray(expected)) return Array.isArray(actual) && actual.length === expected.length && expected.every((v, i) => equal(actual[i], v, false));
    if (expected && typeof expected === 'object') {
      if (!actual || typeof actual !== 'object' || Array.isArray(actual)) return false;
      const keys = Object.keys(expected).filter(key => expected[key] !== undefined);
      return (merge && keys.length > 0 || Object.keys(actual).length === keys.length) && keys.every(key => equal(actual[key], expected[key], merge));
    }
    return actual === expected;
  };
  for (const [path, expected] of Object.entries(fields)) {
    if (expected === undefined || ['createdAt', 'updatedAt'].includes(path)) continue;
    const actual = (mergeMaps ? [path] : path.split('.')).reduce((value, key) => value?.[key], current as any);
    if (!equal(actual, expected, mergeMaps)) throw new Error(`Saved content differs on readback: ${path}. Refresh before retrying.`);
  }
}
